import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { downloadStore, userStore, libraryStore, wishlistStore, bookStore } from '../../store';
import { BookCardDownload, InternetMessage, Loader } from '../../components/shared';
import { BookOpen, Crown, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LibraryScreen: React.FC = observer(() => {
  const navigate = useNavigate();

  useEffect(() => {
    if (userStore.isLoggedIn) {
      downloadStore.getDownloadsByUser();
      libraryStore.getOwnedLibrary();
      wishlistStore.load();
    }
  }, []);

  if (!userStore.isLoggedIn) {
    return (
      <div className="h-full flex flex-col bg-background">
        <div className="safe-area-top bg-background">
          <div className="px-5 pt-4 pb-2">
            <h1 className="text-xl font-bold text-foreground">My Library</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Your books</p>
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-8">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
            <BookOpen className="w-7 h-7 text-muted-foreground/50" />
          </div>
          <p className="text-foreground text-sm font-medium mb-1">Sign in to see your library</p>
          <p className="text-muted-foreground text-xs text-center mb-6">
            Log in to access your owned and downloaded books
          </p>
          <Button variant="secondary" onClick={() => navigate('/login')}>
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  const owned = libraryStore.owned;
  const downloads = downloadStore.downloads;
  const wishlist = wishlistStore.entries;
  const isEmpty = owned.length === 0 && downloads.length === 0 && wishlist.length === 0;

  const handleOpenOwned = (entryId: string) => {
    const entry = libraryStore.owned.find((e) => e._id === entryId);
    if (!entry) return;
    // toJS() deproxies MobX so React Router can clone navigation state.
    navigate('/pdf-viewer', {
      state: {
        download: {
          _id: entry._id,
          user: userStore.user?._id ?? '',
          adverbook: toJS(entry.book),
          createdAt: entry.purchasedAt,
        },
        owned: true,
      },
    });
  };

  return (
    <div className="h-full flex flex-col bg-background">
      <Loader visible={downloadStore.loading || libraryStore.loading} />
      <InternetMessage />

      {/* Header */}
      <div className="safe-area-top bg-background">
        <div className="px-5 pt-4 pb-2">
          <h1 className="text-xl font-bold text-foreground">My Library</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {owned.length} owned · {downloads.length} ad-supported · {wishlist.length} wishlist
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
        {/* Owned (ad-free) */}
        {owned.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-4 h-4 text-[#fdce03] fill-[#fdce03]" />
              <h2 className="text-sm font-semibold text-foreground">Owned · ad-free</h2>
            </div>
            <div className="space-y-3">
              {owned.map((entry) => (
                <button
                  key={entry._id}
                  onClick={() => handleOpenOwned(entry._id)}
                  className="w-full text-left rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors p-3 flex gap-3 items-center"
                >
                  <div className="w-14 h-20 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                    {entry.book.coverImage ? (
                      <img src={entry.book.coverImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                        {entry.book.title?.[0] ?? '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{entry.book.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{entry.book.authorName}</p>
                    <p className="text-[11px] text-[#fdce03] mt-1">Read ad-free</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Wishlist */}
        {wishlist.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Heart className="w-4 h-4 text-red-400 fill-red-400" />
              <h2 className="text-sm font-semibold text-foreground">Wishlist</h2>
            </div>
            <div className="space-y-3">
              {wishlist.map((entry) => (
                <button
                  key={entry._id}
                  onClick={() => {
                    bookStore.setSelectedBook(toJS(entry.book));
                    navigate('/book');
                  }}
                  className="w-full text-left rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors p-3 flex gap-3 items-center"
                >
                  <div className="w-14 h-20 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                    {entry.book.coverImage ? (
                      <img src={entry.book.coverImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                        {entry.book.title?.[0] ?? '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{entry.book.title}</p>
                    <p className="text-xs text-muted-foreground truncate">{entry.book.authorName}</p>
                    <p className="text-[11px] text-red-400 mt-1">Saved · tap to open</p>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {/* Ad-supported downloads */}
        {downloads.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-4 h-4 text-muted-foreground" />
              <h2 className="text-sm font-semibold text-foreground">Ad-supported reading</h2>
            </div>
            <div className="space-y-3">
              {downloads.map((download) => (
                <BookCardDownload key={download._id} download={download} />
              ))}
            </div>
          </section>
        )}

        {isEmpty && (
          <div className="flex flex-col items-center justify-center pt-16">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-7 h-7 text-muted-foreground/50" />
            </div>
            <p className="text-foreground text-sm font-medium mb-1">Your library is empty</p>
            <p className="text-muted-foreground text-xs mb-6">Browse books to start reading</p>
            <Button variant="secondary" onClick={() => navigate('/app/home')}>
              Browse Books
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

export default LibraryScreen;
